import type { ReactNode } from 'react';

// ─── Indicator display labels ───────────────────────────────

const INDICATOR_DISPLAY: Record<string, string> = {
  BB_UPPER: 'BB Upper', BB_MIDDLE: 'BB Middle', BB_LOWER: 'BB Lower', BB_STD: 'BB StdDev',
  KELTNER_UPPER: 'Keltner Upper', KELTNER_MID: 'Keltner Mid', KELTNER_LOWER: 'Keltner Lower',
  DONCHIAN_UPPER: 'Donchian Upper', DONCHIAN_LOWER: 'Donchian Lower', DONCHIAN_MID: 'Donchian Mid',
  CHANDELIER_EXIT_LONG: 'Chandelier Long', CHANDELIER_EXIT_SHORT: 'Chandelier Short',
  RANGE_HIGH: 'Range High', RANGE_LOW: 'Range Low', RANGE_WIDTH: 'Range Width',
  HIGHEST_HIGH: 'Highest High', LOWEST_LOW: 'Lowest Low',
  VOLUME_SMA: 'Vol SMA', FORCE_INDEX: 'Force Index',
  MACD_SIGNAL: 'MACD Signal', MACD_HISTOGRAM: 'MACD Hist',
  STOCH_K: 'Stoch %K', STOCH_D: 'Stoch %D', STOCH_RSI: 'Stoch RSI',
  WILLIAMS_R: 'Williams %R', AWESOME_OSC: 'Awesome Osc', ULTIMATE_OSC: 'Ultimate Osc',
  DMI_PLUS: '+DI', DMI_MINUS: '−DI',
  AROON_UP: 'Aroon Up', AROON_DOWN: 'Aroon Down', AROON_OSC: 'Aroon Osc',
  LINEAR_REG_SLOPE: 'LinReg Slope',
  MEDIAN_PRICE: 'Median Price', TYPICAL_PRICE: 'Typical Price', WEIGHTED_CLOSE: 'Wtd Close',
  ICHIMOKU_TENKAN: 'Tenkan', ICHIMOKU_KIJUN: 'Kijun',
  ICHIMOKU_SENKOU_A: 'Senkou A', ICHIMOKU_SENKOU_B: 'Senkou B', ICHIMOKU_CHIKOU: 'Chikou',
  ELDER_RAY_BULL: 'Elder Bull', ELDER_RAY_BEAR: 'Elder Bear',
  PIVOT_PP: 'Pivot PP', PIVOT_R1: 'Pivot R1', PIVOT_S1: 'Pivot S1', PIVOT_R2: 'Pivot R2', PIVOT_S2: 'Pivot S2',
};

// ─── Types (mirrors condition-engine.types.ts from API) ──────

interface ValueRef {
  type: 'CONSTANT' | 'PRICE' | 'INDICATOR' | 'POSITION' | 'ACCOUNT' | 'TIME';
  value?: number;
  field?: string;
  indicator?: string;
  params?: { period?: number; stdDev?: number; fastPeriod?: number; slowPeriod?: number; signalPeriod?: number; kPeriod?: number; acceleration?: number; maxAcceleration?: number; multiplier?: number };
  offset?: number;
}

interface ComparisonCondition {
  type: 'COMPARISON';
  left: ValueRef;
  op: string;
  right: ValueRef;
}

interface ConditionGroup {
  type: 'AND' | 'OR';
  conditions: Condition[];
}

type Condition = ComparisonCondition | ConditionGroup;

interface LotSizing {
  type: string;
  volume?: number;
  riskPct?: number;
  pct?: number;
  multiplier?: number;
  baseLotRiskFactor?: number;
}

// ─── Highlight chip (matches .hi from strategy-viewer.html) ──

// Color variants matching the viewer's .hi-v, .hi-b, .hi-g, .hi-r, .hi-a, .hi-c, .hi-p
const hiStyles: Record<string, string> = {
  violet: 'bg-violet/10 text-violet',
  blue: 'bg-blue/10 text-blue',
  green: 'bg-green/10 text-green',
  red: 'bg-red/10 text-red',
  amber: 'bg-amber/10 text-amber',
  cyan: 'bg-cyan/10 text-cyan',
  pink: 'bg-pink/10 text-pink',
};

function Hi({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span className={`inline font-bold font-mono text-[10px] px-1.5 py-px rounded ${hiStyles[color] ?? hiStyles.blue}`}>
      {children}
    </span>
  );
}

// ─── Format a ValueRef into a highlighted chip ─────────────

export function fmtRef(ref: ValueRef | null | undefined): ReactNode {
  if (!ref) return '?';
  switch (ref.type) {
    case 'CONSTANT':
      return <Hi color="blue">{ref.value}</Hi>;
    case 'PRICE': {
      const priceOffset = ref.offset ? ` [${ref.offset} bars ago]` : '';
      return <Hi color="blue">{ref.field ?? 'PRICE'}{priceOffset}</Hi>;
    }
    case 'INDICATOR': {
      const parts: string[] = [];
      const pr = ref.params;
      if (pr?.period) parts.push(String(pr.period));
      if (pr?.stdDev) parts.push(`σ${pr.stdDev}`);
      if (pr?.fastPeriod || pr?.slowPeriod) {
        parts.push(`${pr.fastPeriod ?? 12}/${pr.slowPeriod ?? 26}/${pr.signalPeriod ?? 9}`);
      } else if (pr?.signalPeriod) {
        parts.push(`sig:${pr.signalPeriod}`);
      }
      if (pr?.acceleration != null) parts.push(`step:${pr.acceleration}`);
      if (pr?.maxAcceleration != null) parts.push(`max:${pr.maxAcceleration}`);
      if (pr?.multiplier != null) parts.push(`×${pr.multiplier}`);
      const p = parts.length ? `(${parts.join(', ')})` : '';
      const offset = ref.offset ? ` [${ref.offset} bars ago]` : '';
      const displayName = INDICATOR_DISPLAY[ref.indicator ?? ''] ?? ref.indicator;
      return <Hi color="violet">{displayName}{p}{offset}</Hi>;
    }
    case 'POSITION':
      return <Hi color="amber">{(ref.field ?? '').replace(/_/g, ' ')}</Hi>;
    case 'ACCOUNT':
      return <Hi color="cyan">{(ref.field ?? '').replace(/_/g, ' ')}</Hi>;
    case 'TIME':
      return <Hi color="pink">{(ref.field ?? '').replace(/_/g, ' ')}</Hi>;
    default:
      return '?';
  }
}

// ─── Format an operator ─────────────────────────────────────

export function fmtOp(op: string): ReactNode {
  const map: Record<string, ReactNode> = {
    '>': <span className="text-ink-3 font-mono">&gt;</span>,
    '<': <span className="text-ink-3 font-mono">&lt;</span>,
    '>=': <span className="text-ink-3 font-mono">≥</span>,
    '<=': <span className="text-ink-3 font-mono">≤</span>,
    '==': <span className="text-ink-3 font-mono">=</span>,
    CROSSES_ABOVE: <Hi color="pink">crosses above</Hi>,
    CROSSES_BELOW: <Hi color="pink">crosses below</Hi>,
  };
  return map[op] ?? <span className="text-ink-3 font-mono">{op}</span>;
}

// ─── Recursively convert a Condition tree to inline JSX ─────

export function condToSentence(cond: unknown): ReactNode {
  if (!cond || typeof cond !== 'object') return null;

  const c = cond as Record<string, unknown>;

  if (c.type === 'COMPARISON') {
    const left = c.left as ValueRef | undefined;
    const right = c.right as ValueRef | undefined;
    const op = c.op as string;

    // Trivial always-true: 1 == 1
    if (
      left?.type === 'CONSTANT' && left.value === 1 &&
      right?.type === 'CONSTANT' && right.value === 1
    ) {
      return <em className="text-ink-4">Always active</em>;
    }

    return (
      <span className="inline-flex items-center gap-1 flex-wrap">
        {fmtRef(left)} {fmtOp(op)} {fmtRef(right)}
      </span>
    );
  }

  if (c.type === 'AND' || c.type === 'OR') {
    const conditions = c.conditions as Condition[];
    if (!Array.isArray(conditions) || conditions.length === 0) return null;

    return (
      <span className="inline-flex items-center gap-1 flex-wrap">
        {conditions.map((child, i) => (
          <span key={i} className="inline-flex items-center gap-1 flex-wrap">
            {i > 0 && (
              c.type === 'AND'
                ? <strong className="text-blue font-extrabold text-[10px]">AND</strong>
                : <strong className="text-orange font-extrabold text-[10px]">OR</strong>
            )}
            {condToSentence(child)}
          </span>
        ))}
      </span>
    );
  }

  return null;
}

// ─── Format lot sizing to human-readable text ───────────────

export function fmtLot(lot: unknown): ReactNode {
  if (!lot || typeof lot !== 'object') return 'Unknown';
  const l = lot as LotSizing;
  switch (l.type) {
    case 'FIXED':
      return (
        <span>Fixed <Hi color="blue">{l.volume ?? 0}</Hi> lots</span>
      );
    case 'RISK_BASED':
      return (
        <span>Risk-based <Hi color="blue">{l.riskPct ?? 0}%</Hi></span>
      );
    case 'EQUITY_PCT':
      return (
        <span>Equity <Hi color="blue">{l.pct ?? 0}%</Hi></span>
      );
    case 'MARTINGALE':
      return (
        <span>Martingale <Hi color="amber">{l.multiplier}×</Hi> (base {l.baseLotRiskFactor})</span>
      );
    default:
      return l.type || 'Unknown';
  }
}

// ─── Extract all unique indicators from a rules object ──────

export function extractIndicators(rules: Record<string, unknown>): string[] {
  const indicators = new Set<string>();

  function walk(cond: unknown) {
    if (!cond || typeof cond !== 'object') return;
    const c = cond as Record<string, unknown>;

    if (c.type === 'COMPARISON') {
      for (const side of [c.left, c.right]) {
        const ref = side as ValueRef | undefined;
        if (ref?.type === 'INDICATOR') {
          const parts: string[] = [];
          const pr = ref.params;
          if (pr?.period) parts.push(String(pr.period));
          if (pr?.stdDev) parts.push(`σ${pr.stdDev}`);
          if (pr?.fastPeriod || pr?.slowPeriod) {
            parts.push(`${pr.fastPeriod ?? 12}/${pr.slowPeriod ?? 26}/${pr.signalPeriod ?? 9}`);
          } else if (pr?.signalPeriod) {
            parts.push(`sig:${pr.signalPeriod}`);
          }
          if (pr?.acceleration != null) parts.push(`step:${pr.acceleration}`);
          if (pr?.maxAcceleration != null) parts.push(`max:${pr.maxAcceleration}`);
          if (pr?.multiplier != null) parts.push(`×${pr.multiplier}`);
          const p = parts.length ? `(${parts.join(', ')})` : '';
          indicators.add(`${ref.indicator}${p}`);
        }
      }
    }
    if (Array.isArray(c.conditions)) {
      (c.conditions as unknown[]).forEach(walk);
    }
  }

  const entry = rules.entry as Record<string, unknown> | undefined;
  const exit = rules.exit as Record<string, unknown> | undefined;

  if (entry && Array.isArray(entry.rules)) {
    for (const rule of entry.rules as Record<string, unknown>[]) {
      walk(rule.conditions);
      walk(rule.buyCondition);
      walk(rule.sellCondition);
    }
  }
  if (exit && Array.isArray(exit.rules)) {
    for (const rule of exit.rules as Record<string, unknown>[]) {
      walk(rule.conditions);
    }
  }

  return [...indicators];
}
